import React, { useState, useMemo, useEffect, useRef } from "react";
import { Filter } from 'bad-words';
import {
    Box,
    Type,
    Input,
    Btn,
    P,
    HiddenInput,
    FieldSelect,
    FieldText,
    FieldRadio,
    FieldDate,
} from '@saladbob/sassafras'
import dayjs from "dayjs";
import provinces from "../../data/provinces";
import { options as tshirtOptions } from "../../data/tshirtOptions";
import { options as expOptions } from "../../data/experienceOptions";

import type { User } from "../../types";

import Styles from './Fields.module.css';

const filter = new Filter();

const checkTag = async (value: string, id: string) => {
    if (!value || value === '@') return false;
    const val = value.replace('@', '').toLowerCase();
    const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/user?where[mackertaglc][equals]=${val}&where[id][not_in]=${id}`).then((r) => r.json());
    return res.docs?.length > 0;
};

export function Select(props) {
    return (
        <FieldSelect
            bgColor="neutral50"
            bdColor="neutral50"
            placeholder="Select"
            font="p"
            {...props}
        />
    );
}

export function Tshirt(props) {
    const newProps = {
        name: 'tshirt',
        ...props,
    };

    return (
        <Select
            options={tshirtOptions}
            label="T-Shirt Size"
            rules={{ required: true }}
            {...newProps}
        />
    )
};

export function Text(props) {
    return (
        <FieldText
            bgColor="neutral50"
            bdColor="neutral50"
            font="p"
            {...props}
        />
    );
}

export function MackerTagField({ font = 'p', label='Macker Tag', size = 'md', name = 'mackertag', user, ...props }: { label?: string, name?: string, user?: User, font?: string, [key: string]: any }) {
    const [mackertag, setMackertag] = useState(user?.mackertag ? `@${user.mackertag}` : '@');
    const handleKeyDown = (e) => {
        const selectionStart = e.target.selectionStart;
        const allowedKeys = /^[a-zA-Z0-9-_]$/;

        if ((mackertag === '@' || selectionStart === 1) && (e.key === 'Backspace' || e.key === 'Delete' || e.key === 'ArrowLeft')) {
            e.preventDefault();
            return;
        }

        // Allow special keys (Backspace, Arrow keys, etc.)
        if (["Backspace", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)) {
            return;
        }
        // Prevent input if key is not allowed
        if (!allowedKeys.test(e.key)) {
            e.preventDefault();
            return;
        }
    };

    const handleMackerTag = (e) => {
        const value = e.target.value;
        // Ensure it starts with '@'
        if (!value.startsWith("@")) {
            setMackertag('@' + value.replace(/[^a-zA-Z0-9-_]/g, '')); // Replace any invalid characters
            return;
        } else {
            setMackertag('@' + value.substring(1).replace(/[^a-zA-Z0-9-_]/g, '')); // Remove the '@' and replace any invalid characters
        }
    };
    return (
        <>
            <FieldText
                bgColor="neutral50"
                bdColor="neutral50"
                font={font}
                size={size}
                name={name}
                onKeyDown={handleKeyDown}
                onInput={handleMackerTag}
                value={mackertag}
                label={label}
                rules={{
                    required: true,
                    minlength: 3,
                    badWords: (value) => {
                        return {
                            valid: !filter.isProfane(value),
                            message: 'Profanity is not allowed in MackerTag names.',
                        };
                    },
                    checkUnique: async (value) => {
                        const valid = await checkTag(value, user?.id);
                        return {
                            valid: !valid,
                            message: 'This Macker Tag is already taken.',
                        };
                    },
                }}
                {...props}
            />
            <P font="small" mt="xs">
                Your Macker Tag is your unique identifier on the platform. It must be at least 3 characters long and can only contain letters, numbers, dashes, and underscores. It cannot contain profanity and must be unique to you.
            </P>
        </>
    );
}

export function Radio (props) {
    return (
        <FieldRadio
            font="p"
            checkedColor="secondary"
            {...props}
        />
    );
}

export function State(props) {
    const newProps = {
        name: 'state',
        ...props,
    };
    return (
        <Select
            font="p"
            options={provinces}
            label="State"
            rules={{ required: true, minlength: 2 }}
            {...newProps}
        />
    );
}

export function Country(props) {
    const newProps = {
        name: 'country',
        ...props,
    };
    return (
        <Select
            options={[{
                label: 'United States',
                value: 'US',
            }, {
                label: 'Canada',
                value: 'CA',
            }]}
            label="Country"
            rules={{ required: true, minlength: 2 }}
            {...newProps}
        />
    );
}

export function DOB({ value, ...props}) {
    return (
        <FieldDate
            font="p"
            bgColor="neutral50"
            bdColor="neutral50"
            value={value ? dayjs(value).format('MM/DD/YYYY') : null}
            label="Date of Birth"
            name="dob"
            maxDate={ new Date() }
            {...props}
        />
    )
}

export function Experience(props) {
    return (
        <Select
            options={expOptions}
            label="Experience"
            name="experience"
            {...props}
        />
    )
}

export const SearchSelect = ({ value: defaultValue = [], rounded = 'sm', name, data = [], onInput, label, font = 'p', itemsChanged, ...props } : {
    data?: { label: string, value: string }[],
    rounded?: string,
    onInput?: (val: string) => void,
    label?: string,
    font?: string,
    name: string,
    value?: { label: string, value: string }[],
    itemsChanged?: (item: { label: string, value: string }[]) => void,
}) => {
    const inputEl = useRef(null);
    const dropDownEl = useRef(null);
    const selectEl = useRef([]);
    const [isFocus, setIsFocus] = useState(false);
    const [selected, setSelected] = useState(0);
    const [items, setItems] = useState(defaultValue as { label: string, value: string }[]);
    const [show, setShow] = useState(false);
    const [inputValue, setInputValue] = useState('');

    const searchItems = useMemo(() => {
        if (items.length === 0) {
            return data;
        }
        const itemValues = items.map((item) => item.value);
        return data.filter((item) => itemValues.includes(item.value) === false);
    }, [data]);

    const value = useMemo(() => {
        return items.map((item) => item.value).toString();
    }
    , [items]);

    const handleInput = (event: React.FormEvent<HTMLInputElement>) => {
        const val = (event.target as HTMLInputElement).value;
        setInputValue(val);
        if (typeof onInput === 'function') {
            onInput(val);
        }
    }

    const handleSearchItemClick = (item) => {
        setInputValue('');
        setItems([...items, item]);
        setSelected(0);
        if (typeof onInput === 'function') {
            onInput('');
        }
        setTimeout(() => {
            inputEl?.current?.focus();
        }, 100);
    }

    const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            if (searchItems.length) {
                handleSearchItemClick(searchItems[selected]);
            }
        }
        if (event.key === 'Backspace' && inputValue === '') {
            setItems(items.slice(0, items.length - 1));
        };
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setSelected(Math.min(selected + 1, searchItems.length - 1));
        }
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            setSelected(Math.max(selected - 1, 0));
        }
    };

    const removeItem = (item) => {
        setItems(items.filter((i) => i.value !== item.value));
        setTimeout(() => {
            inputEl?.current?.focus();
        }, 100);
    }

    const handleFocus = () => {
        setIsFocus(true);
        setShow(true);
    }

    const handleBlur = () => {
        setIsFocus(false);
        setTimeout(() => {
            setShow(false);
        }, 100);
    }

    useEffect(() => {
        if (typeof itemsChanged === 'function') {
            itemsChanged(items);
        }
    }, [items]);

    useEffect(() => {
        if (show && searchItems.length > 0) {
            if (dropDownEl.current) {
                const topPosition = selectEl.current[selected].offsetTop;
                const bottomPosition = selectEl.current[selected].offsetTop + selectEl.current[selected].offsetHeight;
                const containerHeight = dropDownEl.current.clientHeight;
                const containerScrollTop = dropDownEl.current.scrollTop;
                if (bottomPosition > containerHeight + containerScrollTop) {
                    dropDownEl.current.scrollTop = bottomPosition - containerHeight;
                } else if (topPosition < containerScrollTop) {
                    dropDownEl.current.scrollTop = selectEl.current[selected].offsetTop;
                }
            }
        }
    }, [selected]);

    return (
        <Box>
            <Type htmlFor={name} tag="label" font={font} weight={ isFocus ? 'bold' : null } mb="xs">{label}</Type>
            <Box
                display="flex"
                align="center"
                wrap="wrap"
                gap="xs"
                bgColor="white"
                bdWidth="1px"
                bdColor="neutral200"
                rounded={show && searchItems.length ? [rounded, rounded, '0', '0']: rounded}
                position="relative"
                role="label"
                minHeight="3rem"
                onClick={() => { inputEl?.current?.focus() }}
                onKeyPress={(e) => { e.stopPropagation() }}
                depth={isFocus ? 'sm' : null}
            >
                {items.map((item) => (
                    <Box
                        key={item.value}
                        width="max-content"
                        bgColor="neutral50"
                        bdWidth="1px"
                        bdColor="neutral200"
                        margin="4px 0 4px 4px"
                        rounded="4px"
                        display="flex"
                        align="center"
                        height="max-content"
                    >
                        <Type font="small" padding="xs" width="max-content" nowrap>{item.label}</Type>
                        <Box
                            tag="button"
                            height="100%"
                            display="inline-flex"
                            align="center"
                            justify="center"
                            padding="xs"
                            role="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                removeItem(item);
                            }}
                        >
                            <Type font="small">
                                x
                            </Type>
                        </Box>
                    </Box>
                ))}
                <Type font="p" display="inline-flex" ms="8px">
                    <input
                        ref={inputEl}
                        type="text"
                        id={name}
                        onInput={handleInput}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        value={inputValue}
                        onKeyDown={handleKeyPress}
                        className={Styles.SearchSelectInput}
                    />
                </Type>
            </Box>
            {show && searchItems.length > 0 && (
                <Box
                    ref={dropDownEl}
                    width="100%"
                    maxHeight="300px"
                    overflow="auto"
                    rounded={['0', '0', rounded, rounded]}
                    bgColor="#222"
                    bdWidth="0 1px 1px 1px"
                    style={{
                        position: 'absolute',
                        zIndex: 1,
                        top: '100%',
                        left: 0,
                    }}
                >
                    {searchItems.map((item, i) => (
                        <div onMouseEnter={() => { console.log('hover'); setSelected(i) }}>
                            <Box
                                ref={(ref) => selectEl.current[i] = ref}
                                tag="a"
                                href="#"
                                key={item.value}
                                onClick={(e) => { handleSearchItemClick(item) }}
                                bdWidth="1px 0 0 0"
                                bdColor="#444"
                                padding="8px 16px"
                                bgColor={selected === i ? '#333' : 'transparent'}
                            >
                                <Type
                                    font="control"
                                    txtColor="#ddd"
                                >
                                    {item.label}
                                </Type>
                            </Box>
                        </div>
                    ))}
                </Box>
            )}
            <HiddenInput value={value} name={name} />
        </Box>
    )
};